import { FC, useRef, useState } from "react";
import { TabsProvider } from "../../context/tabsContext";
import { useUserSettings } from "../../context/userSettingsContext";
import { useI18n } from "../../hooks/useTranslations";
import CarDocuments from "../carDocuments/CarDocuments";
import CarPlates from "../carPlates/CarPlates";
import CarTasks from "../carTask/CarTasks";
import Claims from "../claims/Claims";
import Costs from "../costs/Costs";
import DueDates from "../dueDates/DueDates";
import Fines from "../fines/Fines";
import HighwayPasses from "../highwayPasses/HighwayPasses";
import Maintenances from "../maintenances/Maintenances";
import Mileages from "../mileages/Mileages";
import RentalCars from "../rentalCars/RentalCars";
import Header from "../ui/Header";
import Tabs, { TabContent, TabsContents } from "../ui/Tabs";
import CarAssignments from "./CarAssignments";
import Overview, { OverviewPropsHandle } from "./Overview";

const EditCar: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.t.cars.edit} />
      <TabsProvider>
        <TabbedEditCar />
      </TabsProvider>
    </>
  );
};

export default EditCar;

const TabbedEditCar: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const [showCarPlatesTab, setShowCarPlatesTab] = useState(false);
  const [error, setError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const overviewRef = useRef<OverviewPropsHandle>(null);

  const onUpdate = () => overviewRef.current?.reloadData();

  const tabs = [{ title: i18n.t.common.overview }];

  const tabComponents: TabContent[] = [
    {
      body: <Overview ref={overviewRef} setShowCarPlatesTab={setShowCarPlatesTab} setError={setError} />,
      eager: true,
    },
  ];

  if (!error) {
    if (userSettings.permissions.carDrivers.read) {
      tabs.push({ title: i18n.t.carDrivers.plural });
      tabComponents.push({
        body: <CarAssignments onUpdate={onUpdate} />,
        withBorder: true,
      });
    }
    if (showCarPlatesTab && userSettings.permissions.carPlates.read) {
      tabs.push({ title: i18n.t.carPlates.plural });
      tabComponents.push({
        body: <CarPlates onUpdate={onUpdate} />,
        withBorder: true,
      });
    }
    if (userSettings.permissions.carTasks.read) {
      tabs.push({ title: i18n.t.common.tasks });
      tabComponents.push({
        body: <CarTasks />,
        withBorder: true,
      });
    }
    if (userSettings.permissions.highwayPasses.read) {
      tabs.push({ title: i18n.t.highwayPasses.plural });
      tabComponents.push({
        body: <HighwayPasses onUpdate={onUpdate} />,
        withBorder: true,
      });
    }
    if (userSettings.permissions.mileages.read) {
      tabs.push({ title: i18n.t.mileages.plural });
      tabComponents.push({
        body: <Mileages onUpdate={onUpdate} />,
        withBorder: true,
      });
    }
    if (userSettings.permissions.maintenances.read) {
      tabs.push({ title: i18n.t.maintenance.plural });
      tabComponents.push({
        body: <Maintenances onUpdate={onUpdate} />,
        withBorder: true,
      });
    }
    if (userSettings.permissions.costs.read) {
      tabs.push({ title: i18n.t.costs.plural });
      tabComponents.push({
        body: <Costs />,
        withBorder: true,
      });
    }
    if (userSettings.permissions.dueDates.read) {
      tabs.push({ title: i18n.t.dueDates.plural });
      tabComponents.push({
        body: <DueDates onUpdate={onUpdate} />,
        withBorder: true,
      });
    }
    tabs.push({ title: i18n.t.common.documents });
    tabComponents.push({
      body: <CarDocuments setIsDragging={setIsDragging} />,
      withBorder: true,
    });

    if (userSettings.permissions.rentalCars.read) {
      tabs.push({ title: i18n.t.rentalCars.plural });
      tabComponents.push({
        body: <RentalCars onUpdate={onUpdate} />,
        withBorder: true,
      });
    }

    if (userSettings.permissions.claims.read) {
      tabs.push({ title: i18n.t.claims.plural });
      tabComponents.push({
        body: <Claims onUpdate={onUpdate} redirectUrl={window.location.pathname + window.location.search} />,
        withBorder: true,
      });
    }

    if (userSettings.permissions.fines.read) {
      tabs.push({ title: i18n.t.fines.plural });
      tabComponents.push({
        body: <Fines onUpdate={onUpdate} redirectUrl={window.location.pathname + window.location.search} />,
        withBorder: true,
      });
    }
  }

  return (
    <>
      <Tabs tabs={tabs} />
      <TabsContents contents={tabComponents} isDragging={isDragging} />
    </>
  );
};
